import { useState } from "react";
import ReactDatePicker from "react-datepicker";
import { useTranslation } from "react-i18next";
import { Form, Icon, Modal, Radio, Search } from "semantic-ui-react";

import { DATE_FORMATS_FNS, INVOICING_OPTIONS, useCan } from "components";
import { createFormHelper } from "lib/Form";
import { ACL_ACTIVITIES, ACL_RESOURCES } from "models";
import { LOCATION_LICENSE_SUBPAGE } from "modules/LocationLicenseOverview";
import { useLocationLicensesDashboardFilters } from "modules/LocationLicenseOverview/components/LocationLicenseFilters/hooks";
import "modules/LocationLicenseOverview/components/StandaloneLicenseModal/StandaloneLicenseModal.scss";
import { useSelectedStandaloneLicenceCreateMutation, useSelectedStandaloneLicenceUpdateMutation } from "modules/LocationLicenseOverview/hooks";
import { filterLicenseData, findExactItemById, getNextMonthlyInvoicingDate } from "modules/LocationLicenseOverview/utils";
import { formatCurrentUTCDate, formatDate, toDate } from "utils/dateHelperFunctions";
import { formatPrice } from "utils/formatPrice";

import type { ExactStandaloneLicense } from "models";
import type { LicenseType } from "modules/LocationLicenseOverview";
import type { StandaloneLicensePayload } from "modules/LocationLicenseOverview/hooks";
import type { LicenseItem } from "modules/LocationLicenseOverview/queryKeys";
import type { SearchResultData } from "semantic-ui-react";

type StandaloneLicenseModalProps = {
  items: LicenseItem[];
  isOpen: boolean;
  selectedTab: LOCATION_LICENSE_SUBPAGE;
  selectedLicense: LicenseType | null;
  isLoadingLicenses: boolean;
  onClose: () => void;
};

type StandaloneModalLicense = {
  item: LicenseItem | null;
  price: number;
  search: string;
  quantity: number;
  description: string | null;
  searchedItems: LicenseItem[];
  isSearchDropdownOpen: boolean;
};

const defaultLicenseState: StandaloneModalLicense = {
  search: "",
  searchedItems: [],
  item: null,
  description: null,
  price: 0,
  quantity: 0,
  isSearchDropdownOpen: false
};

const MIN_QUANTITY = 1;

const defaultFormValues: ExactStandaloneLicense = {
  created_on: "",
  updated_on: "",
  id: -1,
  exact_item_id: "",
  quantity: 0,
  price: 0,
  description: "",
  invoice_on: null,
  invoiced: false,
  dealer_location_id: -1
};

const FormHelper = createFormHelper<ExactStandaloneLicense>();

export const StandaloneLicenseModal = ({ onClose, isOpen, selectedLicense, items, selectedTab, isLoadingLicenses }: StandaloneLicenseModalProps) => {
  const { t } = useTranslation();

  const [selectedInvoicingOption, setSelectedInvoicingOption] = useState<INVOICING_OPTIONS | null>(null);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [license, setLicense] = useState<StandaloneModalLicense>(defaultLicenseState);
  const [customInvoicingDate, setCustomInvoicingDate] = useState<Date | null>(null);
  const [standaloneLicenseID, setStandaloneLicenseID] = useState<number | null>(null);
  const [hasValidationError, setHasValidationError] = useState(false);

  const { mutateAsync: createStandaloneLicense, isPending: isCreateStandaloneLicensePending } = useSelectedStandaloneLicenceCreateMutation();
  const { mutateAsync: updateStandaloneLicense, isPending: isUpdateStandaloneLicensePending } = useSelectedStandaloneLicenceUpdateMutation();
  const { location: selectedLocation } = useLocationLicensesDashboardFilters();
  const canUpdateLicense = useCan(ACL_ACTIVITIES.Update, ACL_RESOURCES.ExactLicense);

  const closeModal = () => {
    setSelectedInvoicingOption(null);
    setLicense(defaultLicenseState);
    setCustomInvoicingDate(null);
    setStandaloneLicenseID(null);
    setIsDatePickerOpen(false);
    setHasValidationError(false);
    onClose();
  };

  const handleOpenModal = () => {
    if (!selectedLicense?.id) return;
    const item = findExactItemById(items, selectedLicense.exact_item_id);
    if (!item) return;

    setLicense(prevLicense => ({
      ...prevLicense,
      quantity: selectedLicense.quantity ?? MIN_QUANTITY,
      item: item,
      description: selectedLicense.description || "",
      price: selectedLicense.price || item.StandardSalesPrice || 0,
      search: `${item.Code} - ${item.Description}`,
      isSearchDropdownOpen: false
    }));

    setStandaloneLicenseID(selectedLicense.id);
    if (selectedTab === LOCATION_LICENSE_SUBPAGE.MonthlyLicenses) return setSelectedInvoicingOption(INVOICING_OPTIONS.Monthly);

    if (selectedLicense.invoiceOn) setCustomInvoicingDate(new Date(selectedLicense.invoiceOn));
    setSelectedInvoicingOption(INVOICING_OPTIONS.OnceOnDate);
  };

  const handleSearch = (value: string) => {
    const filteredValues = filterLicenseData(items, value);

    setLicense(prevLicense => ({
      ...prevLicense,
      search: value,
      searchedItems: filteredValues,
      isSearchDropdownOpen: true
    }));
  };

  const handleItemClick = (data: SearchResultData) => {
    const item = data.result;
    setLicense(prevLicense => ({
      ...prevLicense,
      item: item || null,
      price: item?.StandardSalesPrice || 0,
      search: item ? `${item.Code} - ${item.Description}` : "",
      searchedItems: [],
      isSearchDropdownOpen: false
    }));
  };

  const prepareSaveParams = () => {
    if (!license.item || !license.price || selectedInvoicingOption === null || !license.quantity || !selectedLocation) {
      setHasValidationError(true);
      return false;
    }

    setHasValidationError(false);
    const params: StandaloneLicensePayload = {
      license: {
        id: -1,
        created_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat),
        dealer_location_id: selectedLocation.id,
        exact_item_id: license.item.ID,
        quantity: license.quantity,
        description: license.description || "",
        price: license.price,
        invoice_on: null,
        invoiced: false
      }
    };
    if (selectedInvoicingOption !== INVOICING_OPTIONS.Monthly) {
      params.license.invoice_on =
        selectedInvoicingOption === INVOICING_OPTIONS.OnceOnDate
          ? (customInvoicingDate?.toISOString() ?? null)
          : formatDate(getNextMonthlyInvoicingDate(), DATE_FORMATS_FNS.ISOFormatStartOfDayWithZ);
    }

    return params;
  };

  const handleQuantity = (quantity: number) => setLicense(license => ({ ...license, quantity, isSearchDropdownOpen: false }));

  const handleDescription = (value: string) => {
    setLicense(prevLicense => ({
      ...prevLicense,
      description: value || "",
      isSearchDropdownOpen: false
    }));
  };

  const handlePrice = (price: number) => setLicense(license => ({ ...license, price, isSearchDropdownOpen: false }));

  const handleCreate = async (params: StandaloneLicensePayload) => {
    await createStandaloneLicense({ license: { ...params.license, dealer_location_id: selectedLocation?.id ?? -1 } });
    closeModal();
  };

  const handleUpdate = async (params: StandaloneLicensePayload) => {
    await updateStandaloneLicense({
      license: { ...params.license, id: standaloneLicenseID ?? -1, updated_on: formatCurrentUTCDate(DATE_FORMATS_FNS.ISOFormat) }
    });
    closeModal();
  };

  const handleSave = () => {
    const params = prepareSaveParams();
    if (!params || (standaloneLicenseID && !canUpdateLicense)) return;
    return standaloneLicenseID ? handleUpdate(params) : handleCreate(params);
  };

  const handleChangeDate = (date: Date | null) => {
    if (!date) return;

    setCustomInvoicingDate(date);
    setSelectedInvoicingOption(INVOICING_OPTIONS.OnceOnDate);
    setIsDatePickerOpen(false);
  };

  return (
    <Modal open={isOpen} size="large" onClose={closeModal} onMount={handleOpenModal} closeOnDimmerClick={false} className="StandaloneLicenseModal">
      <Modal.Header>{t("v8_add_license_manually", "Add license manually")}</Modal.Header>
      <FormHelper.Form onSubmit={handleSave} defaultValues={defaultFormValues} formProps={{ loading: isLoadingLicenses }}>
        <div className="content">
          <Form.Group>
            <Form.Field error={hasValidationError && !license.search} className="dropdown-search" width={7} required>
              <label className="big">{t("v8_license", "License")}</label>
              <div className="search">
                <Search
                  fluid={true}
                  placeholder={t("v8_search_for_license", "Search for license")}
                  icon={<Icon className="magnifying glass" />}
                  noResultsMessage={t("v8_no_results", "No results found")}
                  onSearchChange={(_e, { value }) => handleSearch(value || "")}
                  onResultSelect={(_e, data) => handleItemClick(data)}
                  resultRenderer={({ Code, Description }) => <p>{`${Code} - ${Description}`}</p>}
                  results={license.searchedItems}
                  value={license.search}
                  open={license.isSearchDropdownOpen}
                />
                <Form.Button color="green" type="button" className="rowButton" onClick={() => handleSearch(license.search)}>
                  {t("v8_search", "Search")}
                </Form.Button>
              </div>
            </Form.Field>
            <Form.Field width={5}>
              <label>{t("v8_overwrite_name", "Overwrite name")}</label>
              <FormHelper.Field.Input
                inputProps={{ fluid: true, value: license.description }}
                name="description"
                onChange={description => handleDescription(description)}
              />
            </Form.Field>
            <Form.Field width={2} error={hasValidationError && !license.quantity} required>
              <label>{t("v8_amount", "Amount")}</label>
              <FormHelper.Field.Input
                inputProps={{ fluid: true, type: "number", value: license.quantity }}
                name="quantity"
                onChange={quantity => handleQuantity(quantity)}
              />
            </Form.Field>
            <Form.Field width={2} error={hasValidationError && !license.price} required>
              <label>{t("v8_unit_price", "Unit price")}</label>
              <FormHelper.Field.Input
                inputProps={{
                  fluid: true,
                  type: "text",
                  value: formatPrice(license.price),
                  label: { basic: true, content: "€" },
                  labelPosition: "left"
                }}
                isPriceInput
                name="price"
                onChange={price => handlePrice(price ?? 0)}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field width={16} className="mt-20" error={hasValidationError && selectedInvoicingOption === null} required>
              <label>{t("v8_billing_options", "Billing options")}</label>
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field className="radio-button" width={16}>
              <label>{t("v8_monthly", "Monthly")}</label>
              <Radio
                name="billingOptions"
                value={INVOICING_OPTIONS.Monthly}
                checked={selectedInvoicingOption === INVOICING_OPTIONS.Monthly}
                onChange={() => setSelectedInvoicingOption(INVOICING_OPTIONS.Monthly)}
              />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field className="radio-button" width={16}>
              <label>{`${t("v8_once_on_date", "Once, on date")}: ${customInvoicingDate ? formatDate(customInvoicingDate, DATE_FORMATS_FNS.dateMonthYear) : ""}`}</label>
              <ReactDatePicker
                selected={customInvoicingDate ? toDate(customInvoicingDate) : new Date()}
                onChange={handleChangeDate}
                showMonthDropdown
                showYearDropdown
                minDate={toDate(new Date())}
                dateFormat="dd-MM-yyyy"
                onClickOutside={() => setIsDatePickerOpen(false)}
                onCalendarClose={() => setIsDatePickerOpen(false)}
                onCalendarOpen={() => setIsDatePickerOpen(true)}
                withPortal
                inline={false}
                open={isDatePickerOpen}
                customInput={
                  <Radio
                    name="billingOptions"
                    value={INVOICING_OPTIONS.OnceOnDate}
                    checked={selectedInvoicingOption === INVOICING_OPTIONS.OnceOnDate}
                    onClick={() => setIsDatePickerOpen(true)}
                  />
                }
              />
            </Form.Field>
          </Form.Group>
          <Form.Group>
            <Form.Field className="radio-button" width={16}>
              <label>{t("v8_once_on_next_months_billing_date", "Once, on next month's billing date")}</label>
              <Radio
                name="billingOptions"
                value={INVOICING_OPTIONS.OnceInBillingCycle}
                checked={selectedInvoicingOption === INVOICING_OPTIONS.OnceInBillingCycle}
                onChange={() => setSelectedInvoicingOption(INVOICING_OPTIONS.OnceInBillingCycle)}
              />
            </Form.Field>
          </Form.Group>
        </div>
        <div className="actions">
          <div className="action-buttons">
            <Form.Button className="light" onClick={closeModal}>
              {t("v8_cancel", "Cancel")}
              <Icon className="xmark" />
            </Form.Button>
            <Form.Button
              color="green"
              type="submit"
              disabled={isCreateStandaloneLicensePending || isUpdateStandaloneLicensePending || (Boolean(standaloneLicenseID) && !canUpdateLicense)}
              loading={isCreateStandaloneLicensePending || isUpdateStandaloneLicensePending}
            >
              {t("v8_save", "Save")}
              <Icon className="check" />
            </Form.Button>
          </div>
        </div>
      </FormHelper.Form>
    </Modal>
  );
};
